import { IProductsIndexState } from '.'
import { SortOption } from '../../components/sort-options'
import { IFacet, IFacetSelections } from '../../types/facets'
import trackMkpEvents, { labels } from '../../lib/market-place-track-event'
import { useApolloClient } from '@apollo/client'
import { AlgoliaFilterValue } from '../../lib/algolia'
import { ICategoryPathElement } from '../../types/marketplace-categories'
import { OnSearchFunc } from '@scentregroup/shared/hub-components/searchable-faceted-filter'
import searchFacetOptions from '../../graphql/queries/search-facet-options'
import transformProductFilters from './transform-product-filters'
import { makeProductFilters } from './algolia'
import { groupedFacetOptions } from '../../lib/grouped-facet-options'
import { useCallback } from 'react'

interface IProductIndexEventHandlers {
  onChangeFacetSelections: (facetSelections: IFacetSelections) => void
  onChangeProductTypeSelections: (productTypes: string[]) => void
  onClearAll: () => void
  onChangeSort: (sort: SortOption) => void
  onChangeSortOption: (sortOption: SortOption) => void
  onClickSortOption: () => void
  onChangeOnSale: (onSale: boolean) => void
  onChangeItemsPerPage: (itemsPerPage: number) => void
  onClickItemsPerPage: () => void
  onClickPaginationLink: (page: number, itemsPerPage: number) => void
}

export function makeProductIndexEventHandlers(
  updateUrlSearchString: (
    update: Partial<IProductsIndexState>,
    enableScrollToTop?: boolean
  ) => void,
  setItemsPerPagePreference: (cookieValue: string) => void
): IProductIndexEventHandlers {
  const onChangeFacetSelections = (facetSelections: IFacetSelections): void =>
    updateUrlSearchString(
      {
        page: undefined,
        facetSelections,
      },
      false
    )

  const onChangeProductTypeSelections = (productTypes: string[]): void =>
    updateUrlSearchString(
      {
        page: undefined,
        productTypes,
      },
      false
    )

  const onClearAll = (): void =>
    updateUrlSearchString({
      page: undefined,
      facetSelections: [],
      productTypes: [],
    })

  const onChangeSort = (sort: SortOption): void => {
    updateUrlSearchString({
      page: undefined,
      sort,
    })
    trackMkpEvents.selected(labels.createSortByOption(sort))
  }

  const onChangeSortOption = (sort: SortOption): void => {
    updateUrlSearchString({
      page: undefined,
      sort,
    })
    trackMkpEvents.selected(labels.createSortByOption(sort))
  }

  const onClickSortOption = (): void => trackMkpEvents.expanded(labels.SORT_BY)

  const onChangeOnSale = (value: boolean): void => {
    updateUrlSearchString({
      page: undefined,
      onSale: value,
    })

    trackMkpEvents.clicked(labels.createSaleItems(value))
  }

  const onChangeItemsPerPage = (itemsPerPage: number): void => {
    updateUrlSearchString({
      page: undefined,
      itemsPerPage,
    })
    setItemsPerPagePreference(itemsPerPage.toString())
    trackMkpEvents.selected(labels.createItemsPerPage(itemsPerPage.toString()))
  }

  const onClickItemsPerPage = (): void =>
    trackMkpEvents.expanded(labels.ITEMS_PER_PAGE_EXPANDED)

  const onClickPaginationLink = (page: number, itemsPerPage: number): void => {
    setItemsPerPagePreference(itemsPerPage.toString())
    trackMkpEvents.clicked(labels.createPageNumber((page + 1).toString()))
  }

  return {
    onChangeFacetSelections,
    onChangeProductTypeSelections,
    onClearAll,
    onChangeSort,
    onChangeOnSale,
    onChangeSortOption,
    onClickSortOption,
    onChangeItemsPerPage,
    onClickItemsPerPage,
    onClickPaginationLink,
  }
}

export function useFacetOptionSearch(
  productsIndexState: IProductsIndexState,
  otherFilters?: {
    attribute: string
    values: AlgoliaFilterValue[]
  }[],
  path?: ICategoryPathElement[]
): OnSearchFunc {
  const apolloClient = useApolloClient()
  return useCallback<OnSearchFunc>(
    async (id, _unfilteredOptionGroups, searchTerm) => {
      const {
        data: { facet },
      } = await apolloClient.query<{ facet: IFacet }>({
        query: searchFacetOptions,
        variables: {
          id: id,
          categoryPath: path?.map(cpe => cpe.code) ?? [],
          search: searchTerm,
          filter: transformProductFilters(
            makeProductFilters(
              path?.map(cpe => cpe.code) ?? [],
              productsIndexState,
              otherFilters
            )
          ),
          query: productsIndexState.query,
        },
      })
      return groupedFacetOptions(facet.options, facet.id)
    },
    // Removed apolloClient to prevent unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productsIndexState, otherFilters, path]
  )
}
